/* eslint-disable react/prop-types */
import { useState, createContext, useEffect } from "react";
import Cookies from "js-cookie";
import axios from "axios";
import { API_ROUTES } from "utils/constants";
import { encryptData, decryptData } from "utils/encryption";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";

export const AuthContext = createContext({
  statusAuth: null,
  setStatusAuth: () => {},
  user: {},
  setUser: () => {},
  accessToken: null,
  csrftoken: null,
  setAccessToken: () => {},
  setCSRFToken: () => {},
  isLoading: false,
  isLoggedOut: false,
  setIsLoggedOut: () => {},
});

export function AuthContextProvider(props) {
  const [user, setUser] = useState(() => {
    const storedUser = Cookies.get("user");
    if (storedUser) {
      try {
        // Thử giải mã cookie
        const decryptedUser = decryptData(storedUser);
        if (decryptedUser) {
          return decryptedUser;
        }
        // Nếu giải mã thất bại, thử parse như chuỗi JSON plaintext (cookie cũ)
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser && typeof parsedUser === "object") {
          // Lưu lại user dưới dạng mã hóa
          const encryptedUser = encryptData(parsedUser);
          if (encryptedUser) {
            const expirationDate = new Date();
            expirationDate.setDate(expirationDate.getDate() + 1);
            Cookies.set("user", encryptedUser, {
              expires: expirationDate,
              secure: false,
              sameSite: "Lax",
            });
          }
          return parsedUser;
        }
        console.error("Failed to decrypt or parse user from cookies");
        return {};
      } catch (error) {
        console.error("Error processing user cookie:", error);
        return {};
      }
    }
    return {};
  });
  const [statusAuth, setStatusAuth] = useState(
    () => Cookies.get("statusAuth") || null
  );
  const [accessToken, setAccessToken] = useState(null);
  const [csrftoken, setCSRFToken] = useState(
    () => Cookies.get("csrftoken") || null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggedOut, setIsLoggedOut] = useState(
    () => Cookies.get("isLoggedOut") === "true" || false
  );
  const [alert, setAlert] = useState(null);

  const showAlertAndRedirect = (message, severity) => {
    setAlert({ message, severity });
    setTimeout(() => {
      setAlert(null);
      setAccessToken(null);
      setCSRFToken(null);
      setStatusAuth(null);
      setUser({});
      setIsLoggedOut(true);
      Cookies.remove("user");
      Cookies.remove("statusAuth");
      Cookies.remove("csrftoken");
      Cookies.remove("isLoggedOut");
      window.location.href = "/sign-in";
    }, 3000);
  };

  useEffect(() => {
    let isMounted = true;

    const restoreAuthState = async () => {
      const isLoggedOutFromCookie = Cookies.get("isLoggedOut") === "true";

      if (isLoggedOut || isLoggedOutFromCookie) {
        setAccessToken(null);
        setCSRFToken(null);
        setStatusAuth(null);
        setUser({});
        Cookies.remove("user");
        Cookies.remove("statusAuth");
        Cookies.remove("csrftoken");
        Cookies.remove("isLoggedOut");
        setIsLoading(false);
        return;
      }

      if (!accessToken && Object.keys(user).length > 0) {
        let newAccessToken = null;
        let newUser = {};

        try {
          const refreshResponse = await axios.post(
            API_ROUTES.REFRESH_TOKEN,
            {},
            {
              withCredentials: true,
              headers: {
                Accept: "application/json",
              },
            }
          );

          if (refreshResponse.status === 200 && isMounted) {
            newAccessToken = refreshResponse.data.access_token;
            const newCSRFToken = refreshResponse.headers["x-csrftoken"] || null;

            if (!newAccessToken) {
              throw new Error("Access token missing in refresh token response");
            }

            const userResponse = await axios.get(
              `${API_ROUTES.BASE_URL}/user`,
              {
                headers: {
                  Authorization: `Bearer ${newAccessToken}`,
                  Accept: "application/json",
                },
                withCredentials: true,
              }
            );

            if (userResponse.status === 200 && isMounted) {
              newUser = userResponse.data;
            } else {
              throw new Error("Failed to fetch user data after refresh");
            }

            const expirationDate = new Date();
            expirationDate.setDate(expirationDate.getDate() + 1);
            const encryptedUser = encryptData(newUser);
            if (encryptedUser) {
              Cookies.set("user", encryptedUser, {
                expires: expirationDate,
                secure: false,
                sameSite: "Lax",
              });
            } else {
              throw new Error("Failed to encrypt user data");
            }
            Cookies.set("statusAuth", "login", {
              expires: expirationDate,
              secure: false,
              sameSite: "Lax",
            });
            Cookies.set("csrftoken", newCSRFToken, {
              expires: expirationDate,
              secure: false,
              sameSite: "Lax",
            });

            if (isMounted) {
              setAccessToken(newAccessToken);
              setCSRFToken(newCSRFToken);
              setStatusAuth("login");
              setUser(newUser);
            }
          } else {
            throw new Error(
              `Refresh token failed with status: ${refreshResponse.status}`
            );
          }
        } catch (error) {
          if (isMounted) {
            if (
              error.response?.status === 401 &&
              error.response?.data?.detail === "Token is blacklisted"
            ) {
              showAlertAndRedirect(
                "Phiên đăng nhập của bạn đã hết hạn do token bị vô hiệu hóa. Vui lòng đăng nhập lại.",
                "error"
              );
            } else if (
              error.message.includes("Failed to fetch") ||
              error.message.includes("Network")
            ) {
              console.log("Network error, keeping current state");
            }
          }
        } finally {
          if (isMounted) {
            setIsLoading(false);
          }
        }
      } else {
        setIsLoading(false);
      }
    };

    const minimumLoadingTime = 500;
    const startTime = Date.now();

    restoreAuthState().finally(() => {
      const elapsedTime = Date.now() - startTime;
      const remainingTime = minimumLoadingTime - elapsedTime;
      if (remainingTime > 0) {
        setTimeout(() => {
          if (isMounted) {
            setIsLoading(false);
          }
        }, remainingTime);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [isLoggedOut]);

  return (
    <>
      {alert && (
        <Box
          sx={{
            position: "fixed",
            top: 20,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            width: "90%",
            maxWidth: 600,
          }}
        >
          <Alert severity={alert.severity}>{alert.message}</Alert>
        </Box>
      )}
      <AuthContext.Provider
        value={{
          setStatusAuth,
          statusAuth,
          user,
          setUser,
          accessToken,
          setAccessToken,
          csrftoken,
          setCSRFToken,
          isLoading,
          isLoggedOut,
          setIsLoggedOut,
        }}
      >
        {props.children}
      </AuthContext.Provider>
    </>
  );
}

export default AuthContext;
